import React from 'react';
import { createRoot } from 'react-dom/client';
import ChatWidget from '../components/ChatWidget.jsx';
import { createSession } from '../utils/api.js';

(function () {
  let root = null;
  let container = null;
  let config = {};
  let sessionId = null;

  const defaultConfig = {
    darkMode: false,
    primaryColor: '#2563eb',
    position: 'bottom-right',
    companyName: 'Support Team',
    conciergeName: null,
    companyLogo: null,
    welcomeMessage: 'Hi! How can we help?',
    quickQuestions: [],
    quickQuestionsLayout: 'vertical',
    textColor: '#fcfcfc',
    agentMessageBubbleColor: undefined,
    userMessageBoxColor: undefined,
    assistantTextColor: undefined,
    userTextColor: undefined,
    fontFamily: 'Inter',
    fontSize: '14px',
    defaultLanguage: 'en',
    autoOpen: false,
    openDelay: 0,
    apiBaseUrl: null,
    apiKey: null,
    organizationId: null,
    sessionUrl: null,
    userId: null,
    userName: null,
    userEmail: null,
    locale: 'en',
    headerTextBold: false,
    headerTextItalic: false,
    enableVoiceInteraction: false,
    disclaimerText: undefined,
    disclaimerPosition: 'top',
    showPoweredBy: true,
    onboardingQuestions: [],
    onboardingEnabled: false,
    collectionPrompt: undefined,
    allowedDomains: [],
    onOpen: null,
    onClose: null,
  };

  const API = {
    /**
     * Initialize the chat widget
     * @param {Object} options - Configuration options
     */
    init: async function (options = {}) {
      config = { ...defaultConfig, ...options };

      // Create session if sessionUrl provided
      if (config.sessionUrl && !sessionId) {
        sessionId = await createSession(config);
      }

      // Create container if doesn't exist
      if (!container) {
        container = document.createElement('div');
        container.id = 'chat-widget-root';
        container.className = 'chat-widget-container';
        document.body.appendChild(container);
      }

      // Create React root
      if (!root) {
        root = createRoot(container);
      }

      // Detect system dark mode if not explicitly set
      if (options.darkMode === undefined) {
        config.darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      }

      // Render component
      // If orgId and apiBaseUrl are provided, use dynamic config
      // Otherwise, use the old static config approach for backward compatibility
      const widgetProps = config.orgId && config.apiBaseUrl
        ? {
          // Required props
          orgId: config.orgId,
          apiBaseUrl: config.apiBaseUrl,
          // Only pass props that were explicitly provided in options (not from defaults)
          // This allows API response values to be used when props are not provided
          ...(options.position !== undefined && { position: config.position }),
          ...(options.quickQuestionsLayout !== undefined && { quickQuestionsLayout: config.quickQuestionsLayout }),
          ...(options.darkMode !== undefined && { darkMode: config.darkMode }),
          ...(options.primaryColor !== undefined && { primaryColor: config.primaryColor }),
          ...(options.companyName !== undefined && { companyName: config.companyName }),
          ...(options.conciergeName !== undefined && { conciergeName: config.conciergeName }),
          ...(options.companyLogo !== undefined && { companyLogo: config.companyLogo }),
          ...(options.welcomeMessage !== undefined && { welcomeMessage: config.welcomeMessage }),
          ...(options.quickQuestions !== undefined && { quickQuestions: config.quickQuestions }),
          ...(options.textColor !== undefined && { textColor: config.textColor }),
          ...(options.agentMessageBubbleColor !== undefined && { agentMessageBubbleColor: config.agentMessageBubbleColor }),
          ...(options.userMessageBoxColor !== undefined && { userMessageBoxColor: config.userMessageBoxColor }),
          ...(options.assistantTextColor !== undefined && { assistantTextColor: config.assistantTextColor }),
          ...(options.userTextColor !== undefined && { userTextColor: config.userTextColor }),
          ...(options.fontFamily !== undefined && { fontFamily: config.fontFamily }),
          ...(options.fontSize !== undefined && { fontSize: config.fontSize }),
          ...(options.defaultLanguage !== undefined && { defaultLanguage: config.defaultLanguage }),
          ...(options.locale !== undefined && { locale: config.locale }),
          ...(options.headerTextBold !== undefined && { headerTextBold: config.headerTextBold }),
          ...(options.headerTextItalic !== undefined && { headerTextItalic: config.headerTextItalic }),
          ...(options.enableVoiceInteraction !== undefined && { enableVoiceInteraction: config.enableVoiceInteraction }),
          ...(options.disclaimerText !== undefined && { disclaimerText: config.disclaimerText }),
          ...(options.disclaimerPosition !== undefined && { disclaimerPosition: config.disclaimerPosition }),
          ...(options.onboardingQuestions !== undefined && { onboardingQuestions: config.onboardingQuestions }),
          ...(options.onboardingEnabled !== undefined && { onboardingEnabled: config.onboardingEnabled }),
          ...(options.showPoweredBy !== undefined && { showPoweredBy: config.showPoweredBy }),
          ...(options.collectionPrompt !== undefined && { collectionPrompt: config.collectionPrompt }),
          // Only pass allowedDomains if explicitly provided (not from default empty array)
          ...(options.allowedDomains !== undefined && options.allowedDomains.length > 0 && { allowedDomains: config.allowedDomains }),
          ...(options.autoOpen !== undefined && { autoOpen: config.autoOpen }),
          ...(options.openDelay !== undefined && { openDelay: config.openDelay }),
          ...(options.apiKey !== undefined && { apiKey: config.apiKey }),
          ...(options.className !== undefined && { className: config.className }),
        }
        : {
          // Backward compatibility: pass all props as before
          ...config,
          apiBaseUrl: config.apiBaseUrl,
          organizationId: config.organizationId,
        };

      root.render(
        React.createElement(ChatWidget, widgetProps)
      );

      // Call onOpen callback if provided
      if (config.onOpen && config.autoOpen) {
        setTimeout(() => config.onOpen(), config.openDelay);
      }

      return API;
    },

    /**
     * Update configuration
     * @param {Object} newConfig - New configuration options
     */
    updateConfig: function (newConfig) {
      config = { ...config, ...newConfig };
      this.init(config);
      return API;
    },

    /**
     * Open the chat widget
     */
    open: function () {
      // Trigger open by updating config
      this.updateConfig({ autoOpen: true, openDelay: 0 });
      if (config.onOpen) config.onOpen();
      return API;
    },

    /**
     * Close the chat widget
     */
    close: function () {
      this.updateConfig({ autoOpen: false });
      if (config.onClose) config.onClose();
      return API;
    },

    /**
     * Toggle dark mode
     */
    toggleTheme: function () {
      config.darkMode = !config.darkMode;
      this.updateConfig({ darkMode: config.darkMode });
      return API;
    },


    /**
     * Destroy the widget
     */
    destroy: function () {
      if (root) {
        root.unmount();
        root = null;
      }
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
        container = null;
      }
      sessionId = null;
      config = {};
      return API;
    },

    /**
     * Get current configuration
     */
    getConfig: function () {
      return { ...config };
    }
  };

  // Auto-initialize if config exists in window
  if (typeof window !== 'undefined') {
    window.ChatWidget = API;

    // Auto-init with window.ChatWidgetConfig
    if (window.ChatWidgetConfig) {
      document.addEventListener('DOMContentLoaded', () => {
        API.init(window.ChatWidgetConfig);
      });
    }

    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (config.darkMode === undefined || config.autoTheme) {
        API.updateConfig({ darkMode: e.matches });
      }
    });
  }

  // CommonJS export
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = API;
  }
})();